import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Instagram, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { CONTACT, waLink } from "@/lib/contact";
import { Reveal, MaskText } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { Seo } from "@/components/Seo";
import ProductCard from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1545191050-96042d612b1f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const STORY_IMG = "https://images.unsplash.com/photo-1629231484522-f86ab156c34a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Home() {
  const { t, tr, L, catLabel } = useLang();
  const [collections, setCollections] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [fighters, setFighters] = useState([]);
  const [instagram, setInstagram] = useState([]);
  const [hero, setHero] = useState({});
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  useEffect(() => {
    api.get("/collections").then((r) => setCollections(r.data));
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8)));
    api.get("/fighters").then((r) => setFighters(r.data));
    api.get("/instagram").then((r) => setInstagram(r.data));
    api.get("/site").then((r) => setHero(r.data.hero || {}));
  }, []);

  return (
    <div data-testid="home-page">
      <Seo path="" />
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] overflow-hidden">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          {/* Capacidad de video: reemplazable por footage de entrenamiento/competencia */}
          <img src={hero.home || HERO} alt="Atleta de VORNA entrenando combate" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        </motion.div>
        <motion.div style={{ y: textY }} className="relative h-full mx-auto max-w-[1600px] px-5 lg:px-10 flex flex-col justify-end pb-20 lg:pb-28">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="mb-5 text-xs font-semibold tracking-[0.35em] text-vorna-accent uppercase">
            {tr("Nacido en Ecuador · Deportes de Combate", "Born in Ecuador · Combat Sports")}
          </motion.p>
          <h1 className="font-display uppercase leading-[0.82] text-[15vw] sm:text-[12vw] lg:text-[8.5vw] tracking-tighter">
            <MaskText lines={tr(["Hecho para", "la pelea."], ["Built for", "the fight."])} />
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
            className="mt-5 max-w-xl text-white/70 text-base lg:text-lg">
            {tr("La marca ecuatoriana de deportes de combate. Productos para atletas de MMA, Jiu-Jitsu y grappling.",
                "The Ecuadorian combat-sports brand. Gear for MMA, Jiu-Jitsu and grappling athletes.")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/catalogo" data-testid="hero-catalog-button"
              className="group inline-flex items-center justify-center gap-2 bg-vorna-accent hover:bg-vorna-accentHover px-8 py-4 font-display uppercase tracking-widest text-sm active:scale-95 transition-transform">
              {t("cta_catalog")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/atletas" data-testid="hero-athletes-button"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white px-8 py-4 font-display uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors">
              {t("cta_athletes")}
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <div className="border-y border-white/10 py-5 bg-vorna-bg">
        <Marquee items={tr(["Nacido en Ecuador", "Hecho para la pelea", "MMA · BJJ · Grappling", "@VORNA"], ["Born in Ecuador", "Built for the fight", "MMA · BJJ · Grappling", "@VORNA"])} accent speed={35} />
      </div>

      {/* COLECCIONES */}
      <section className="mx-auto max-w-[1600px] px-5 lg:px-10 py-20 lg:py-32">
        <Reveal>
          <div className="flex items-end justify-between mb-10 lg:mb-14">
            <h2 className="font-display uppercase tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.85]">
              {tr("Explora el", "Browse the")}<br /><span className="text-stroke">{tr("Catálogo", "Catalogue")}</span>
            </h2>
            <Link to="/catalogo" className="hidden sm:inline-flex items-center gap-2 text-sm tracking-widest hover:text-vorna-accent transition-colors">
              {tr("VER TODO", "VIEW ALL")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 auto-rows-[42vw] sm:auto-rows-[26vw] lg:auto-rows-[20vw]">
          {collections.map((c, i) => {
            const big = i === 0 || i === 3;
            return (
              <Reveal key={c.key} delay={i * 0.05} className={big ? "col-span-2" : ""}>
                <Link to={`/catalogo?category=${c.key}`} data-testid={`collection-${c.key}`} className="group relative block h-full w-full overflow-hidden bg-vorna-elevated">
                  <img src={c.image} alt={L(c, "title")} loading="lazy" className="h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-5 lg:p-7">
                    <h3 className="font-display uppercase tracking-tight text-xl lg:text-3xl leading-none">{L(c, "title")}</h3>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs tracking-widest text-white/60 group-hover:text-vorna-accent transition-colors">{tr("VER", "VIEW")} <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* PRODUCTOS DESTACADOS */}
      <section className="bg-vorna-surface border-y border-white/10 py-20 lg:py-32">
        <div className="mx-auto max-w-[1600px] px-5 lg:px-10">
          <Reveal>
            <p className="text-xs tracking-[0.35em] text-vorna-accent uppercase mb-3">{tr("Destacados", "Featured")}</p>
            <h2 className="font-display uppercase tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.85] mb-10 lg:mb-14">{tr("Productos", "Products")}</h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {featured.map((p, i) => <Reveal key={p.id} delay={(i % 4) * 0.05}><ProductCard product={p} /></Reveal>)}
          </div>
          <div className="mt-12 text-center">
            <Link to="/catalogo" className="inline-flex items-center gap-2 border border-white/25 px-8 py-4 font-display uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors">
              {t("cta_catalog")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ATLETAS */}
      <section className="mx-auto max-w-[1600px] px-5 lg:px-10 py-20 lg:py-32">
        <Reveal>
          <p className="text-xs tracking-[0.35em] text-vorna-accent uppercase mb-3">{tr("Respaldados en la jaula", "Trusted in the cage")}</p>
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end mb-12">
            <h2 className="font-display uppercase tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.85] max-w-3xl">
              {tr("Representados por atletas que viven el deporte", "Represented by athletes who live the sport")}
            </h2>
            <p className="max-w-sm text-sm text-white/50 leading-relaxed">
              {tr("Desde los campamentos en Ecuador hasta la competencia profesional, VORNA es llevado por peleadores reales.",
                  "From training camps in Ecuador to professional competition, VORNA is worn by real fighters.")}
            </p>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
          {fighters.map((f, i) => (
            <Reveal key={f.id} delay={i * 0.05}>
              <Link to={`/atletas/${f.id}`} data-testid={`cage-fighter-${f.id}`} className="group relative block aspect-[3/4] overflow-hidden bg-vorna-elevated">
                <img src={f.image} alt={f.name} loading="lazy" className="h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <p className="font-display uppercase text-sm lg:text-base leading-none">{f.name}</p>
                  <p className="text-[11px] text-vorna-accent mt-1">"{f.nickname}"</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/atletas" className="inline-flex items-center gap-2 text-vorna-accent font-display uppercase tracking-widest text-sm hover:gap-3 transition-all">{t("cta_athletes")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      {/* HISTORIA */}
      <section className="relative">
        <div className="grid lg:grid-cols-2">
          <div className="relative h-[60vh] lg:h-auto overflow-hidden">
            <img src={hero.story || STORY_IMG} alt="VORNA Ecuador" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-black/30" />
          </div>
          <div className="bg-vorna-bg flex items-center px-6 lg:px-20 py-20">
            <Reveal>
              <p className="text-xs tracking-[0.35em] text-vorna-accent uppercase mb-5">{t("nav_about")}</p>
              <h2 className="font-display uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.85]">
                {tr("Nacido en Ecuador.", "Born in Ecuador.")}<br /><span className="text-stroke-accent">{tr("Hecho para la pelea.", "Built for the fight.")}</span>
              </h2>
              <p className="mt-6 max-w-md text-white/60 leading-relaxed">
                {tr("VORNA nació de la comunidad ecuatoriana de deportes de combate: una marca hecha por peleadores, para peleadores. Disciplina, individualidad y rendimiento en cada prenda.",
                    "VORNA grew out of Ecuador's combat sports community — a brand built by fighters, for fighters. Discipline, individuality and performance in every piece.")}
              </p>
              <Link to="/sobre-vorna" className="mt-8 inline-flex items-center gap-2 border border-white/30 px-7 py-3.5 font-display uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors">
                {t("cta_about")} <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="bg-vorna-surface border-t border-white/10 mx-auto max-w-full px-5 lg:px-10 py-20 lg:py-28">
        <div className="mx-auto max-w-[1600px]">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
              <h2 className="font-display uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.85]">{tr("Sigue la pelea", "Follow the fight")}</h2>
              <a data-testid="instagram-follow" href={CONTACT.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-vorna-accent font-display uppercase tracking-widest text-lg hover:gap-3 transition-all"><Instagram className="h-6 w-6" /> @VORNA</a>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {instagram.map((post, i) => (
              <Reveal key={post.id} delay={i * 0.04}>
                <a href={post.link} target="_blank" rel="noreferrer" data-testid={`instagram-post-${post.id}`} className="group relative block aspect-square overflow-hidden bg-vorna-elevated">
                  <img src={post.image} alt={post.caption || "VORNA"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-black/75 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-3 text-center">
                    <Instagram className="h-5 w-5 text-vorna-accent" />
                    {post.caption && <p className="text-[11px] text-white/90 leading-snug line-clamp-3">{post.caption}</p>}
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACTO CTA */}
      <section className="bg-vorna-bg py-20 lg:py-28 text-center px-5">
        <Reveal>
          <h2 className="font-display uppercase tracking-tighter text-4xl lg:text-6xl leading-[0.85] mb-5">{tr("¿Te interesa un producto?", "Interested in a product?")}</h2>
          <p className="text-white/60 max-w-xl mx-auto mb-8">{tr("Escríbenos por WhatsApp para consultar disponibilidad, tallas, colores y precios.", "Message us on WhatsApp to ask about availability, sizes, colors and pricing.")}</p>
          <a href={waLink(tr("Hola VORNA, quisiera más información sobre sus productos.", "Hi VORNA, I'd like more info about your products."))} target="_blank" rel="noreferrer" data-testid="home-whatsapp-cta"
            className="inline-flex items-center gap-2 bg-vorna-accent px-8 py-4 font-display uppercase tracking-widest text-sm hover:bg-vorna-accentHover active:scale-95 transition-all"><MessageCircle className="h-4 w-4" /> {t("contact_vorna")}</a>
        </Reveal>
      </section>
    </div>
  );
}
